// Global
import React from 'react';
import { tv } from 'tailwind-variants';

// Local
import { PageSpecific } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';

export type ArticleProps = PageSpecific.Article_Component;

const Article = (props: ArticleProps): JSX.Element => {
  const { Description, Eyebrow, Heading, Subheading, Image, ReadMoreCTA } = props?.fields || {};
  const { RenderingIdentifier, styles } = props?.params || {};

  /*
   * RENDERING
   */

  const customTailwindVariants = tv({
    extend: TAILWIND_VARIANTS,
    slots: {
      base: [styles.trimEnd()],
    },
  });

  const { base, contentContainer, cta, description, eyebrow, heading, subheading, image } =
    customTailwindVariants();
  const isEditing = useIsEditing();

  return (
    <section
      className={base()}
      data-component="authorable/shared/page-specific/article"
      id={RenderingIdentifier}
    >
      <ImageWrapper field={Image} className={image()} />
      <div className={contentContainer()}>
        {(Eyebrow?.value !== '' || isEditing) && (
          <p className={eyebrow()} data-name="eyebrow">
            <PlainTextWrapper editable field={Eyebrow} />
          </p>
        )}
        <RichTextWrapper className={heading()} data-name="heading" field={Heading} />
        <RichTextWrapper className={subheading()} data-name="subheading" field={Subheading} />
        <RichTextWrapper className={description()} data-name="description" field={Description} />
        <LinkWrapper className={cta()} data-name="cta" field={ReadMoreCTA} />
      </div>
    </section>
  );
};

export const Default = withStandardComponentWrapper(Article);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['p-6'],
    contentContainer: ['p-10'],
    cta: ['hidden'],
    description: [],
    eyebrow: ['font-normal', 'mb-2', 'opacity-80', 'text-xs'],
    heading: ['font-bold', 'font-modern', 'mb-2', 'text-4xl'],
    subheading: ['font-bold', 'font-modern', 'mb-2', 'opacity-80', 'text-2xl'],
    image: ['js-image'],
  },
});
